import React, { useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import IaTeaserModeBanner from '@features/IaTeaserModeBanner';
import IssuesGrid from './components/IssuesGrid';
import IssueCountBadge from './components/IssueCountBadge';
import SubmissionsTimeline from './components/SubmissionsTimeline';
import useIIMRuns from './hooks/useIIMRuns';
import { iimReviewKeys } from './hooks/queryKeys';
import { ModeIA } from './types';
import { IIM_REVIEW_COMPLETED_EVENT, IIM_RUN_COMPLETED_EVENT } from './events';
import './styles.scss';
export { IIM_REVIEW_COMPLETED_EVENT, IIM_RUN_COMPLETED_EVENT };
const iimQueryClient = new QueryClient();
function IIMReviewInner({ inspectionId, modeIA, currentUser, ipaScorecardEnabled = false }) {
    const [reviewCompleted, setReviewCompleted] = useState(modeIA === ModeIA.ReadOnly);
    const { data: runs } = useIIMRuns(inspectionId);
    const queryClient = useQueryClient();
    const activeRun = runs === null || runs === void 0 ? void 0 : runs.find((r) => r.status === 'in_progress' || r.status === 'pending');
    const isRunning = !!activeRun;
    // IP-13835: when a run transitions from in_progress → completed, the peer_synthesis
    // Lambda writes fresh data to IpaInspectionAssessment. Invalidate the cached
    // assessment so the card immediately reflects the new run's values.
    const wasRunningRef = useRef(isRunning);
    useEffect(() => {
        const wasRunning = wasRunningRef.current;
        wasRunningRef.current = isRunning;
        if (wasRunning && !isRunning) {
            queryClient.invalidateQueries({ queryKey: iimReviewKeys.inspectionAssessment(inspectionId) });
            document.dispatchEvent(new CustomEvent(IIM_RUN_COMPLETED_EVENT, { detail: { inspectionId } }));
        }
    }, [isRunning, queryClient, inspectionId]);
    useEffect(() => {
        const handler = () => setReviewCompleted(true);
        document.addEventListener(IIM_REVIEW_COMPLETED_EVENT, handler);
        return () => document.removeEventListener(IIM_REVIEW_COMPLETED_EVENT, handler);
    }, []);
    const effectiveModeIA = reviewCompleted ? ModeIA.ReadOnly : modeIA;
    return (React.createElement("div", { className: "iim-review" },
        React.createElement(IssueCountBadge, { inspectionId: inspectionId }),
        React.createElement("div", { className: "iim-review__active-sections" },
            React.createElement(IssuesGrid, { inspectionId: inspectionId, modeIA: effectiveModeIA, isRunning: isRunning, ipaScorecardEnabled: ipaScorecardEnabled })),
        React.createElement(SubmissionsTimeline, { inspectionId: inspectionId }),
        modeIA === ModeIA.Teaser && !currentUser.isInspectpointUser && React.createElement(IaTeaserModeBanner, { bannerMode: "inspection" })));
}
export default function IIMReview({ inspectionId, modeIA, currentUser, ipaScorecardEnabled = false }) {
    return (React.createElement(QueryClientProvider, { client: iimQueryClient },
        React.createElement(IIMReviewInner, { inspectionId: inspectionId, modeIA: modeIA, currentUser: currentUser, ipaScorecardEnabled: ipaScorecardEnabled })));
}
